import {ApiProvider} from "@renta-apps/athenaeum-react-common";
import Localizer from "@/localization/Localizer";
import CreateDeviceTheftAlarmRequest from "@/models/server/Requests/Alarm/CreateDeviceTheftAlarmRequest";
import CreateDeviceUnusedFor3DaysAlarmRequest from "@/models/server/Requests/Alarm/CreateDeviceUnusedFor3DaysAlarmRequest";
import DeviceFuelOrFluidLevelAlertRequest from "@/models/server/Requests/Alarm/DeviceFuelOrFluidLevelAlertRequest";
import CreateVoltageAlarmRequest from "@/models/server/Requests/Alarm/CreateVoltageAlarmRequest";
import AlarmModel from "@/models/server/AlarmModel";
import {AlarmType, EnvironmentDeviceType, FuelSensorType, SubscriptionAlertType} from "@/models/Enums";
import SaveEnvironmentControlAlarmRequest from "@/models/server/Requests/Alarm/SaveEnvironmentControlAlarmRequest";
import AlarmData from "@/models/server/AlarmData";
import BaseAlarmDeviceModel from "@/models/BaseAlarmDeviceModel";
import BaseAlarmModel from "@/models/BaseAlarmModel";

export const subscribeToAlert = async (
    alertType: SubscriptionAlertType,
    device: BaseAlarmDeviceModel<BaseAlarmModel>,
    constructionSiteId: string | null,
    contractId: string | null,
    organizationId: string | null,
    alarmData: AlarmData | undefined,
): Promise<AlarmModel | null> => {
    const {rentaId, fuelSensorType} = device;
    const {value, min, max} = alarmData ?? {};
    if (alertType === SubscriptionAlertType.Idle) {
        return await createDeviceUnusedAlarmAsync(rentaId, constructionSiteId, contractId, organizationId);
    } else if (alertType === SubscriptionAlertType.Fuel && fuelSensorType != null && value != null) {
        return await createFuelAlarmAsync(rentaId, fuelSensorType, constructionSiteId, contractId, organizationId, value);
    } else if (alertType === SubscriptionAlertType.Battery) {
        return await createVoltageAlarmAsync(rentaId, constructionSiteId, contractId, organizationId);
    } else if (alertType === SubscriptionAlertType.Theft) {
        return await createTheftAlarmAsync(rentaId, constructionSiteId, contractId, organizationId);
    } else if (alertType === SubscriptionAlertType.EnvironmentControlStructureInternalTemperature && min != null && max != null) {
        return await createEnvironmentControlAlarmAsync(rentaId, constructionSiteId, contractId, organizationId, min, max, AlarmType.EnvironmentControlStructureTemperature2, EnvironmentDeviceType.Structure, 0);
    }

    return null;
};

export const createEnvironmentControlAlarmAsync = async (rentaId: string,
    constructionSiteId: string | null,
    contractId: string | null,
    organizationId: string | null,
    min: number,
    max: number,
    alarmType: AlarmType,
    environmentDeviceType: EnvironmentDeviceType,
    sensorId: number): Promise<AlarmModel | null> => {
    const request: SaveEnvironmentControlAlarmRequest = {
        name: setAlarmName(alarmType, rentaId),
        rentaId: rentaId,
        constructionSiteId: constructionSiteId,
        contractId: contractId,
        organizationId: organizationId,
        min: min,
        max: max,
        sensorId: sensorId,
        EnvironmentDeviceType: environmentDeviceType,
    }
    return ApiProvider.postAsync<AlarmModel | null>("/api/Alarm/SaveEnvironmentControlAlarm", request, this);
};

export const createFuelAlarmAsync = async (rentaId: string, fuelSensorType: FuelSensorType, constructionSiteId: string | null, contractId: string | null, organizationId: string | null, percentage: number): Promise<AlarmModel | null> => {
    const request: DeviceFuelOrFluidLevelAlertRequest = {
        name: setAlarmName(AlarmType.TrackUnitFuelLevelLessThan, rentaId),
        rentaId: rentaId,
        fuelSensorType: fuelSensorType,
        constructionSiteId: constructionSiteId,
        contractId: contractId,
        organizationId,
        percentage: percentage,
    };

    return ApiProvider.postAsync<AlarmModel | null>("/api/Alarm/CreateDeviceFuelLevelAlarm", request, this);
};

export const createVoltageAlarmAsync = async (rentaId: string, constructionSiteId: string | null, contractId: string | null, organizationId: string | null): Promise<AlarmModel | null> => {
    const request: CreateVoltageAlarmRequest = {
        name: setAlarmName(AlarmType.TrackUnitBatteryVoltageLessThan, rentaId),
        rentaId: rentaId,
        constructionSiteId: constructionSiteId,
        contractId: contractId,
        organizationId: organizationId
    };


    return ApiProvider.postAsync<AlarmModel | null>("/api/Alarm/CreateDeviceBatteryLevelLessThanAlarm", request, this);
};

export const createTheftAlarmAsync = async (rentaId: string, constructionSiteId: string | null, contractId: string | null, organizationId: string | null): Promise<AlarmModel | null> => {
    const request: CreateDeviceTheftAlarmRequest = {
        name: setAlarmName(AlarmType.TrackUnitMovementBasedTheft, rentaId),
        rentaId: rentaId,
        constructionSiteId: constructionSiteId,
        contractId: contractId,
        organizationId: organizationId,
    };

    return ApiProvider.postAsync<AlarmModel | null>("/api/Alarm/CreateDeviceTheftAlarm", request, this);
};

export const createDeviceUnusedAlarmAsync = async (rentaId: string, constructionSiteId: string | null, contractId: string | null, organizationId: string | null): Promise<AlarmModel | null> => {
    const request: CreateDeviceUnusedFor3DaysAlarmRequest = {
        name: setAlarmName(AlarmType.DeviceUnusedFor3Days, rentaId),
        rentaId: rentaId,
        constructionSiteId: constructionSiteId,
        contractId: contractId,
        organizationId: organizationId,
    };

    return ApiProvider.postAsync<AlarmModel | null>("/api/Alarm/CreateDeviceUnusedFor3DaysAlarm", request, this);
};

export const deleteAlarmAsync = async (alarmId: string): Promise<boolean> => {
    return ApiProvider.postAsync<boolean>("/api/Alarm/DeleteAlarm", alarmId, this);
};

const setAlarmName = (alarmType: AlarmType, rentaId: string): string => {
    let alarmName = translateAlertType(alarmType);
    alarmName = alarmName.replace(/ /g, '-').toLowerCase();

    return `${rentaId}-alarm-${alarmName}`;
};

export const translateAlertType = (alarmType: AlarmType) => {
    let alarmName = Localizer.genericNameUnknown;

    switch (alarmType) {
        case AlarmType.TrackUnitDeviceStarts:
            alarmName = Localizer.enumAlarmTypeTrackUnitDeviceStarts;
            break;
        case AlarmType.TrackUnitDeviceStops:
            alarmName = Localizer.enumAlarmTypeTrackUnitDeviceStops;
            break;
        case AlarmType.TrackUnitBatteryVoltageGreaterThan:
            alarmName = Localizer.enumAlarmTypeTrackUnitBatteryVoltageGreaterThan;
            break;
        case AlarmType.TrackUnitBatteryVoltageLessThan:
            alarmName = Localizer.enumAlarmTypeTrackUnitBatteryVoltageLessThan;
            break;
        case AlarmType.TrackUnitFuelLevelGreaterThan:
            alarmName = Localizer.enumAlarmTypeTrackUnitFuelLevelGreaterThan;
            break;
        case AlarmType.TrackUnitFuelLevelLessThan:
            alarmName = Localizer.enumAlarmTypeTrackUnitFuelLevelLessThan;
            break;
        case AlarmType.TrackUnitMovementBasedTheft:
            alarmName = Localizer.enumAlarmTypeTrackUnitMovementBasedTheft;
            break;
        case AlarmType.PindoraFluidLevelLessThan:
            alarmName = Localizer.enumAlarmTypePindoraFluidLevelLessThan;
            break;
        case AlarmType.DeviceUnusedFor3Days:
            alarmName = Localizer.enumAlarmTypeDeviceUnusedFor3Days;
            break;
    }

    return alarmName;
}